import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Send, Phone, Video, VideoOff, Mic, MicOff, 
  PhoneOff, Monitor, MonitorOff, X, MoreVertical,
  ArrowLeft, User, Info, Paperclip, Smile,
  Maximize, Minimize, Hand, Signal, Check, CheckCheck,
  Download, File, Volume2, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  AttachmentPreview,
  MessageReactions,
  EnhancedMessageInput
} from '../components/dm';
import { BACKEND_URL } from '@/config/api';

// Call Quality Indicator
const CallQualityIndicator = ({ stats }) => {
  const getQualityLevel = () => {
    if (!stats) return 'unknown';
    const { latency, packetsLost } = stats;
    if (latency > 300 || packetsLost > 10) return 'poor';
    if (latency > 150 || packetsLost > 5) return 'fair';
    return 'good';
  };
  
  const quality = getQualityLevel();
  const colors = {
    good: 'text-green-500',
    fair: 'text-yellow-500',
    poor: 'text-red-500',
    unknown: 'text-gray-500'
  };
  
  return (
    <div className="flex items-center gap-1">
      <Signal size={12} className={colors[quality]} />
    </div>
  );
};

// Video Call Overlay Component
const VideoCallOverlay = ({ 
  isOpen, 
  onClose, 
  localStream, 
  remoteStream,
  participant,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  audioEnabled,
  videoEnabled,
  screenShareEnabled,
  callStats
}) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 w-64 bg-gray-900 rounded-xl shadow-2xl overflow-hidden z-50 cursor-pointer"
        onClick={() => setIsMinimized(false)}
      >
        <div className="relative aspect-video">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-2 right-2 w-16 aspect-video rounded overflow-hidden border border-white/20">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute top-2 left-2 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-white text-xs">Call in progress</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
            style={{ backgroundColor: participant?.avatar_color || '#6366F1' }}
          >
            {participant?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <h3 className="text-white font-medium">{participant?.name || 'User'}</h3>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <CallQualityIndicator stats={callStats} />
              <span>Connected</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMinimized(true)}
            className="text-gray-400 hover:text-white"
          >
            <Minimize size={20} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-red-400 hover:text-red-300"
          >
            <X size={20} />
          </Button>
        </div>
      </div>

      {/* Video Area */}
      <div className="flex-1 relative p-4">
        {/* Remote Video (large) */}
        <div className="w-full h-full bg-gray-800 rounded-xl overflow-hidden">
          {remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center text-white text-4xl font-bold"
                style={{ backgroundColor: participant?.avatar_color || '#6366F1' }}
              >
                {participant?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
          )}
        </div>
        
        {/* Local Video (small overlay) */}
        <div className="absolute bottom-8 right-8 w-48 aspect-video bg-gray-700 rounded-lg overflow-hidden border-2 border-white/20 shadow-xl">
          {localStream && videoEnabled ? (
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <VideoOff className="text-gray-500" size={24} />
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 p-6 border-t border-white/10">
        <Button
          variant={audioEnabled ? "outline" : "destructive"}
          size="lg"
          className="rounded-full w-14 h-14"
          onClick={onToggleAudio}
        >
          {audioEnabled ? <Mic size={24} /> : <MicOff size={24} />}
        </Button>
        
        <Button
          variant={videoEnabled ? "outline" : "destructive"}
          size="lg"
          className="rounded-full w-14 h-14"
          onClick={onToggleVideo}
        >
          {videoEnabled ? <Video size={24} /> : <VideoOff size={24} />}
        </Button>
        
        <Button
          variant={screenShareEnabled ? "secondary" : "outline"}
          size="lg"
          className="rounded-full w-14 h-14"
          onClick={onToggleScreenShare}
        >
          {screenShareEnabled ? <MonitorOff size={24} /> : <Monitor size={24} />}
        </Button>
        
        <Button
          variant="destructive"
          size="lg"
          className="rounded-full w-14 h-14 bg-red-600 hover:bg-red-700"
          onClick={onClose}
        >
          <PhoneOff size={24} />
        </Button>
      </div>
    </div>
  );
};

// Main DM Chat Page
const DMChatPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Chat state
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagePollingRef = useRef(null);
  
  // Typing indicator state
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [typingUserName, setTypingUserName] = useState('');
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  
  // Read receipts state
  const [lastReadMessageId, setLastReadMessageId] = useState(null);
  
  // Video call state
  const [isInCall, setIsInCall] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [callStats, setCallStats] = useState(null);
  const peerConnectionRef = useRef(null);
  const wsRef = useRef(null);

  // Get the other participant (not current user)
  const otherParticipant = conversation?.participants?.find(p => p.user_id !== user?.id);

  // Fetch conversation details
  const fetchConversation = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dm/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const conversations = await response.json();
        const conv = conversations.find(c => c.id === conversationId);
        if (conv) {
          setConversation(conv);
        }
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/dm/conversations/${conversationId}/messages`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Send typing indicator
  const sendTypingIndicator = async () => {
    const now = Date.now();
    // Only send typing indicator every 2 seconds
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    
    try {
      await fetch(`${BACKEND_URL}/api/dm/conversations/${conversationId}/typing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail
    }
  };

  // Check typing status
  const checkTypingStatus = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/dm/conversations/${conversationId}/typing`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setIsOtherTyping(data.is_typing);
        setTypingUserName(data.user_name || '');
      }
    } catch (error) {
      // Silently fail
    }
  };

  // Mark messages as read
  const markMessagesAsRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/dm/conversations/${conversationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail
    }
  };

  // Get read status
  const fetchReadStatus = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/dm/conversations/${conversationId}/read-status`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setLastReadMessageId(data.last_read_message_id);
      }
    } catch (error) {
      // Silently fail
    }
  };

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (e.target.value.trim()) {
      sendTypingIndicator();
    }
  };

  // Send message
  const handleSendMessage = async (content, attachments = []) => {
    // Handle both form event and direct call
    if (content?.preventDefault) {
      content.preventDefault();
      content = newMessage.trim();
      attachments = [];
    }
    
    if (!content?.trim() && attachments.length === 0) return;
    if (sending) return;
    
    setSending(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/dm/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            content: content?.trim() || '',
            attachments: attachments 
          })
        }
      );
      
      if (response.ok) {
        const message = await response.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Add/remove reactions
  const handleAddReaction = async (messageId, emoji) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/dm/messages/${messageId}/reactions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ emoji })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, reactions: data.reactions } : m
        ));
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleRemoveReaction = async (messageId, emoji) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/dm/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, reactions: data.reactions } : m
        ));
      }
    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial fetch
  useEffect(() => {
    if (conversationId && token) {
      fetchConversation();
      fetchMessages();
      markMessagesAsRead();
      fetchReadStatus();
      
      // Poll for new messages every 3 seconds
      messagePollingRef.current = setInterval(() => {
        fetchMessages();
        checkTypingStatus();
        fetchReadStatus();
      }, 3000);
      
      // Poll typing status more frequently
      typingTimeoutRef.current = setInterval(checkTypingStatus, 1500);
      
      return () => {
        if (messagePollingRef.current) {
          clearInterval(messagePollingRef.current);
        }
        if (typingTimeoutRef.current) {
          clearInterval(typingTimeoutRef.current);
        }
      };
    }
  }, [conversationId, token]);

  // WebRTC setup for video calls
  const startCall = async (withVideo = true) => {
    try {
      // Get local media stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: withVideo,
        audio: true
      });
      setLocalStream(stream);
      setVideoEnabled(withVideo);
      setIsInCall(true);
      
      // Connect to signaling server
      const wsUrl = `${BACKEND_URL.replace('http', 'ws')}/api/calls/ws/dm-call/${conversationId}?token=${token}`;
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected for DM call');
        // Create peer connection
        setupPeerConnection(stream);
      };
      
      wsRef.current.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        await handleSignalingMessage(data);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error');
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket closed');
      };
      
      toast.success(`${withVideo ? 'Video' : 'Audio'} call started`);
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error('Could not access camera/microphone');
    }
  };

  const setupPeerConnection = (stream) => {
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };
    
    peerConnectionRef.current = new RTCPeerConnection(configuration);
    
    // Add local tracks
    stream.getTracks().forEach(track => {
      peerConnectionRef.current.addTrack(track, stream);
    });
    
    // Handle remote stream
    peerConnectionRef.current.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
    };
    
    // Handle ICE candidates
    peerConnectionRef.current.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate
        }));
      }
    };
    
    // Create and send offer
    createOffer();
  };

  const createOffer = async () => {
    try {
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'offer',
          sdp: offer
        }));
      }
    } catch (error) {
      console.error('Error creating offer:', error);
    }
  };

  const handleSignalingMessage = async (data) => {
    try {
      switch (data.type) {
        case 'offer':
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
          const answer = await peerConnectionRef.current.createAnswer();
          await peerConnectionRef.current.setLocalDescription(answer);
          wsRef.current.send(JSON.stringify({
            type: 'answer',
            sdp: answer
          }));
          break;
          
        case 'answer':
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
          break;
          
        case 'ice-candidate':
          if (data.candidate) {
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          }
          break;
          
        case 'call-ended':
          endCall();
          toast.info('Call ended by other participant');
          break;
      }
    } catch (error) {
      console.error('Error handling signaling message:', error);
    }
  };

  const endCall = () => {
    // Stop all tracks
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'call-ended' }));
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setRemoteStream(null);
    setIsInCall(false);
    setVideoEnabled(true);
    setAudioEnabled(true);
    setScreenShareEnabled(false);
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (screenShareEnabled) {
        // Switch back to camera
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        const videoTrack = stream.getVideoTracks()[0];
        
        const sender = peerConnectionRef.current?.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(videoTrack);
        }
        
        // Update local stream
        if (localStream) {
          localStream.getVideoTracks()[0]?.stop();
        }
        setLocalStream(stream);
        setScreenShareEnabled(false);
      } else {
        // Share screen
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];
        
        const sender = peerConnectionRef.current?.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(screenTrack);
        }
        
        screenTrack.onended = () => {
          toggleScreenShare();
        };
        
        setScreenShareEnabled(true);
      }
    } catch (error) {
      console.error('Screen share error:', error);
      toast.error('Could not share screen');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      endCall();
    };
  }, []);

  // Format timestamp
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#1a1d21] ml-[352px]">
        <div className="text-white/50">Loading conversation...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#1a1d21] min-h-0 ml-[352px]" data-testid="dm-chat-page">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/20 bg-gradient-to-r from-[#0d1f35] to-[#1a2942] shadow-lg">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="dm-back-btn"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          
          {conversation ? (
            <>
              <div 
                className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold text-lg shadow-md"
                style={{ backgroundColor: otherParticipant?.avatar_color || '#6366F1' }}
              >
                {otherParticipant?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              
              <div>
                <h2 className="text-white font-semibold text-lg">{otherParticipant?.name || 'User'}</h2>
                <p className="text-emerald-400 text-sm flex items-center gap-1">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                  Online
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-full bg-gray-600 animate-pulse"></div>
              <div>
                <div className="h-5 w-24 bg-gray-600 rounded animate-pulse mb-1"></div>
                <div className="h-4 w-16 bg-gray-600 rounded animate-pulse"></div>
              </div>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => startCall(false)}
            className="text-white hover:text-white hover:bg-white/10 h-10 w-10 rounded-lg"
            title="Audio Call"
            data-testid="dm-audio-call-btn"
          >
            <Phone size={22} />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => startCall(true)}
            className="text-white hover:text-white hover:bg-white/10 h-10 w-10 rounded-lg"
            title="Video Call"
            data-testid="dm-video-call-btn"
          >
            <Video size={22} />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:text-white hover:bg-white/10 h-10 w-10 rounded-lg"
            title="Info"
          >
            <Info size={22} />
          </Button>
        </div>
      </header>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date}>
            {/* Date Divider */}
            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/40 text-xs font-medium">{date}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            
            {/* Messages for this date */}
            {dateMessages.map((message, index) => {
              const isOwnMessage = message.sender_id === user?.id;
              const showAvatar = !isOwnMessage && (
                index === 0 || 
                dateMessages[index - 1]?.sender_id !== message.sender_id
              );
              
              return (
                <div 
                  key={message.id}
                  className={cn(
                    "flex gap-3 mb-2 group",
                    isOwnMessage && "flex-row-reverse"
                  )}
                >
                  {/* Avatar */}
                  <div className="w-8 flex-shrink-0">
                    {showAvatar && !isOwnMessage && (
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                        style={{ backgroundColor: message.sender_avatar_color || '#6366F1' }}
                      >
                        {message.sender_name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                  
                  {/* Message Bubble */}
                  <div className="max-w-[70%]">
                    <div className={cn(
                      "px-4 py-2 rounded-2xl",
                      isOwnMessage 
                        ? "bg-blue-600 text-white rounded-br-md" 
                        : "bg-white/10 text-white rounded-bl-md"
                    )}>
                      {/* Voice message indicator */}
                      {message.is_voice_message && (
                        <div className="flex items-center gap-2 mb-1">
                          <Volume2 size={16} className={isOwnMessage ? "text-blue-200" : "text-white/60"} />
                          <span className="text-xs">Voice message</span>
                        </div>
                      )}
                      
                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {message.attachments.map((att, i) => (
                            <AttachmentPreview key={att.id || i} attachment={att} />
                          ))}
                        </div>
                      )}
                      
                      {/* Text content */}
                      {message.content && (
                        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                      )}
                      
                      {/* Timestamp and read status */}
                      <div className={cn(
                        "flex items-center gap-1 mt-1",
                        isOwnMessage ? "justify-end" : ""
                      )}>
                        <span className={cn(
                          "text-xs",
                          isOwnMessage ? "text-blue-200" : "text-white/40"
                        )}>
                          {formatTime(message.created_at)}
                        </span>
                        {isOwnMessage && (
                          message.is_read ? (
                            <CheckCheck size={14} className="text-blue-200" />
                          ) : (
                            <Check size={14} className="text-blue-300/50" />
                          )
                        )}
                      </div>
                    </div>
                    
                    {/* Reactions */}
                    <MessageReactions
                      reactions={message.reactions || []}
                      currentUserId={user?.id}
                      onAddReaction={(emoji) => handleAddReaction(message.id, emoji)}
                      onRemoveReaction={(emoji) => handleRemoveReaction(message.id, emoji)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4"
              style={{ backgroundColor: otherParticipant?.avatar_color || '#6366F1' }}
            >
              {otherParticipant?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <h3 className="text-white font-semibold text-lg">{otherParticipant?.name}</h3>
            <p className="text-white/50 mt-2">
              This is the beginning of your conversation with {otherParticipant?.name}
            </p>
          </div>
        )}
        
        {/* Typing Indicator */}
        {isOtherTyping && (
          <div className="flex items-center gap-2 px-4 py-2">
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
              style={{ backgroundColor: otherParticipant?.avatar_color || '#6366F1' }}
            >
              {otherParticipant?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex items-center gap-1 text-white/50 text-sm">
              <span>{typingUserName || otherParticipant?.name} is typing</span>
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Enhanced Message Input */}
      <EnhancedMessageInput
        value={newMessage}
        onChange={handleInputChange}
        onSend={handleSendMessage}
        placeholder={`Message ${otherParticipant?.name || 'user'}...`}
        disabled={sending}
        token={token}
        conversationId={conversationId}
      />

      {/* Video Call Overlay */}
      <VideoCallOverlay
        isOpen={isInCall}
        onClose={endCall}
        localStream={localStream}
        remoteStream={remoteStream}
        participant={otherParticipant}
        onToggleAudio={toggleAudio}
        onToggleVideo={toggleVideo}
        onToggleScreenShare={toggleScreenShare}
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
        screenShareEnabled={screenShareEnabled}
        callStats={callStats}
      />
    </div>
  );
};

export default DMChatPage;
